import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";

/**
 * IME入力制御用のカスタムフック
 * 日本語入力時の変換中はsetterを呼ばず、変換確定後にのみsetterを実行する
 *
 * @param value - 現在の値
 * @param setter - 値を更新する関数
 * @param debounceMs - デバウンス時間（ミリ秒）
 * @returns Input要素に渡すプロパティとリアルタイム値
 */
export const useComposingInput = ({
  value,
  setter,
  debounceMs = 500,
}: {
  value: string;
  setter: (value: string) => void;
  debounceMs?: number;
}) => {
  const [composing, setComposing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const lastSetValueRef = useRef<string | undefined>(undefined);
  const isUserInputRef = useRef(false);

  // デバウンス処理付きのsetter
  const debouncedSetter = useDebounceCallback((newValue: string) => {
    lastSetValueRef.current = newValue;
    setter(newValue);
  }, debounceMs);

  // 外部からの値変更を同期（ユーザーの入力による変更は除く）
  useEffect(() => {
    // ユーザーの入力による変更でない場合のみ同期
    if (
      !isUserInputRef.current &&
      value !== inputValue &&
      value !== lastSetValueRef.current
    ) {
      setInputValue(value);
    }
    // ユーザー入力フラグをリセット
    isUserInputRef.current = false;
  }, [value, inputValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      isUserInputRef.current = true;
      setInputValue(newValue);
      // IME変換中でない場合のみsetterを呼ぶ
      if (!composing) {
        debouncedSetter(newValue);
      }
    },
    [composing, debouncedSetter],
  );

  const handleCompositionStart = useCallback(() => {
    setComposing(true);
  }, []);

  const handleCompositionEnd = useCallback(
    (e: React.CompositionEvent<HTMLInputElement>) => {
      setComposing(false);
      const newValue = (e.target as HTMLInputElement).value;
      isUserInputRef.current = true;
      setInputValue(newValue);
      // 変換確定時は即座にsetterを呼ぶ（デバウンスなし）
      lastSetValueRef.current = newValue;
      setter(newValue);
    },
    [setter],
  );

  return {
    value: inputValue,
    onChange: handleChange,
    onCompositionStart: handleCompositionStart,
    onCompositionEnd: handleCompositionEnd,
  };
};
